import { Controller } from '@hotwired/stimulus'
import { renderPdf, watchContainerWidth } from '../lib/pdf_renderer'
import {
  projectPlacement as computeProjection,
  applyProjection,
} from '../lib/placement_projection'

const SIGNER_REF_MIME = 'application/x-signer-ref'
const SIGNER_COLOR_MIME = 'application/x-signer-color'

const HANDLE_DIRECTIONS = ['nw', 'ne', 'se', 'sw']

const HANDLE_POSITION_CLASSES = {
  nw: '-top-1.5 -left-1.5 cursor-nw-resize',
  ne: '-top-1.5 -right-1.5 cursor-ne-resize',
  se: '-bottom-1.5 -right-1.5 cursor-se-resize',
  sw: '-bottom-1.5 -left-1.5 cursor-sw-resize',
}

function interpolate(template, vars) {
  return Object.entries(vars).reduce(
    (output, [key, value]) =>
      output.replaceAll(`%{${key}}`, value === undefined ? '' : String(value)),
    template
  )
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

export default class extends Controller {
  static targets = ['stage', 'loading', 'chipsList', 'counter']

  static values = {
    documentId: Number,
    url: String,
    placementWidth: Number,
    placementHeight: Number,
    minWidth: Number,
    maxWidth: Number,
    minHeight: Number,
    maxHeight: Number,
    initialPlacements: Array,
    signers: Array,
    mode: { type: String, default: 'parallel' },
    remove: String,
    placedLabel: String,
    counterTemplate: String,
    sequentialLabel: { type: String, default: '%{name} (%{index})' },
    unnamedLabel: { type: String, default: 'Signer %{index}' },
  }

  initialize() {
    this.placements = []
    this.placementCounter = 0
    this.pagesMap = new Map()
    this.dragging = false
    this.rendering = false
  }

  async connect() {
    this.bindChipDrag()

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }

    try {
      const renderedPages = await renderPdf(this.urlValue, this.stageTarget)
      this.wireRenderedPages(renderedPages)
      this.hydrateInitialPlacements()
      this.notifyPlacementsChange()
    } catch (error) {
      console.error('PDF render failed', error)
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }
    }

    this.stopWatching = watchContainerWidth(
      this.stageTarget,
      () => this.rerender()
    )
  }

  disconnect() {
    if (this.stopWatching) this.stopWatching()
  }

  async rerender() {
    if (this.rendering || this.dragging) return
    this.rendering = true
    const snapshot = this.placements.map((placement) => ({
      signerRef: placement.signerRef,
      color: placement.color,
      page: placement.page,
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
    }))
    try {
      this.placements = []
      this.placementCounter = 0
      this.pagesMap = new Map()
      this.stageTarget.innerHTML = ''
      const renderedPages = await renderPdf(this.urlValue, this.stageTarget)
      this.wireRenderedPages(renderedPages)
      snapshot.forEach((placement) => this.appendPlacement(placement))
    } catch (error) {
      console.error('PDF re-render failed', error)
    } finally {
      this.rendering = false
    }
  }

  bindChipDrag() {
    if (!this.hasChipsListTarget) return

    this.chipsListTarget.addEventListener('dragstart', (event) => {
      const chip = event.target.closest('[data-signer-ref]')
      if (!chip) return

      event.dataTransfer.setData(SIGNER_REF_MIME, chip.dataset.signerRef)
      event.dataTransfer.setData(
        SIGNER_COLOR_MIME,
        chip.dataset.signerColor || '#028090'
      )
      event.dataTransfer.effectAllowed = 'copy'
    })
  }

  wireRenderedPages(renderedPages) {
    renderedPages.forEach((page) => {
      const wrapper = document.createElement('div')
      wrapper.style.position = 'relative'
      wrapper.style.display = 'block'
      page.canvas.parentNode.insertBefore(wrapper, page.canvas)
      wrapper.appendChild(page.canvas)
      page.wrapper = wrapper

      wrapper.addEventListener('dragover', this.allowDrop)
      wrapper.addEventListener('drop', (event) => this.onDrop(event, page))

      this.pagesMap.set(page.page, page)
    })
  }

  hydrateInitialPlacements() {
    const initial = Array.from(this.initialPlacementsValue || [])
    initial.forEach((placement) => {
      const page = this.pagesMap.get(Number(placement.page_number))
      if (!page) return

      this.appendPlacement({
        signerRef: placement.signer_ref,
        color: placement.color || this.colorForRef(placement.signer_ref),
        page: page.page,
        x: Number(placement.x),
        y: Number(placement.y),
        width: Number(placement.width) || this.placementWidthValue,
        height: Number(placement.height) || this.placementHeightValue,
      })
    })
    this.refreshChipBadges()
    this.refreshCounter()
  }

  allowDrop = (event) => {
    const types = Array.from(event.dataTransfer?.types || [])
    if (!types.includes(SIGNER_REF_MIME)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  onDrop(event, page) {
    const signerRef = event.dataTransfer.getData(SIGNER_REF_MIME)
    if (!signerRef) return

    event.preventDefault()
    const { x, y } = this.computeDropCoordinates(event, page)
    const color = event.dataTransfer.getData(SIGNER_COLOR_MIME) ||
      this.colorForRef(signerRef)
    this.appendPlacement({
      signerRef,
      color,
      page: page.page,
      x,
      y,
    })
    this.refreshChipBadges()
    this.refreshCounter()
    this.notifyPlacementsChange()
  }

  computeDropCoordinates(event, page) {
    const rect = page.canvas.getBoundingClientRect()
    const dynamicScale = rect.width / page.pdfWidth
    const cssX = event.clientX - rect.left
    const cssY = event.clientY - rect.top
    const ptX = cssX / dynamicScale
    const ptYFromTop = cssY / dynamicScale

    const halfWidth = this.placementWidthValue / 2
    const halfHeight = this.placementHeightValue / 2
    const maxX = page.pdfWidth - this.placementWidthValue
    const maxY = page.pdfHeight - this.placementHeightValue
    return {
      x: clamp(ptX - halfWidth, 0, maxX),
      y: clamp(page.pdfHeight - ptYFromTop - halfHeight, 0, maxY),
    }
  }

  appendPlacement({ signerRef, color, page, x, y, width, height }) {
    this.placementCounter += 1
    const placement = {
      id: `p-${this.documentIdValue}-${this.placementCounter}`,
      signerRef,
      color,
      page,
      x,
      y,
      width: width || this.placementWidthValue,
      height: height || this.placementHeightValue,
    }
    this.placements.push(placement)
    this.renderPlacement(placement)
    return placement
  }

  renderPlacement(placement) {
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo || !pageInfo.wrapper) return

    const wrapper = document.createElement('div')
    wrapper.dataset.placementId = placement.id
    wrapper.dataset.placementRef = placement.signerRef
    wrapper.className = [
      'absolute flex items-center justify-center',
      'rounded-md border-2 border-dashed font-medium',
      'cursor-grab active:cursor-grabbing select-none group touch-none',
    ].join(' ')
    wrapper.style.cssText = [
      `background-color: ${placement.color}33`,
      `border-color: ${placement.color}`,
      `color: ${placement.color}`,
    ].join(';')

    const label = document.createElement('span')
    label.dataset.placementLabel = ''
    label.textContent = this.labelForRef(placement.signerRef)
    label.className = 'pointer-events-none px-2 truncate min-w-0'
    wrapper.appendChild(label)

    const removeButton = this.buildRemoveButton(placement)
    wrapper.appendChild(removeButton)

    HANDLE_DIRECTIONS.forEach((direction) => {
      wrapper.appendChild(this.buildResizeHandle(placement, direction))
    })

    wrapper.addEventListener('pointerdown', (event) => {
      this.beginDrag(event, placement)
    })

    placement.element = wrapper
    pageInfo.wrapper.appendChild(wrapper)
    this.reprojectPlacement(placement)
  }

  buildRemoveButton(placement) {
    const button = document.createElement('button')
    button.type = 'button'
    button.title = this.removeValue
    button.setAttribute('aria-label', this.removeValue)
    button.className = [
      'absolute -top-2 -right-2 w-5 h-5 rounded-full',
      'bg-white border border-gray-300 text-gray-600',
      'flex items-center justify-center text-xs leading-none',
      'cursor-pointer hover:bg-red-50 hover:text-red-600',
      'opacity-0 group-hover:opacity-100 transition-opacity z-10',
    ].join(' ')
    button.textContent = '×'
    button.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.removePlacement(placement)
    })
    button.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
    })
    return button
  }

  buildResizeHandle(placement, direction) {
    const handle = document.createElement('div')
    handle.dataset.placementHandle = direction
    handle.className = [
      'absolute w-3 h-3 bg-white border-2 rounded-sm',
      'opacity-0 group-hover:opacity-100 transition-opacity',
      HANDLE_POSITION_CLASSES[direction],
    ].join(' ')
    handle.style.borderColor = placement.color
    handle.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
      this.beginResize(event, placement, direction)
    })
    return handle
  }

  beginDrag(event, placement) {
    if (event.button !== 0) return
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo) return

    event.preventDefault()
    const wrapperRect = placement.element.getBoundingClientRect()
    const offsetCssX = event.clientX - wrapperRect.left
    const offsetCssY = event.clientY - wrapperRect.top

    const onMove = (moveEvent) => {
      this.continueDrag(moveEvent, placement, pageInfo, offsetCssX, offsetCssY)
    }
    const onUp = (upEvent) => {
      try {
        placement.element.releasePointerCapture(upEvent.pointerId)
      } catch (_) { /* noop */ }
      placement.element.removeEventListener('pointermove', onMove)
      placement.element.removeEventListener('pointerup', onUp)
      placement.element.removeEventListener('pointercancel', onUp)
      placement.element.classList.remove('opacity-70')
      this.dragging = false
      this.notifyPlacementsChange()
    }

    this.dragging = true
    placement.element.setPointerCapture(event.pointerId)
    placement.element.classList.add('opacity-70')
    placement.element.addEventListener('pointermove', onMove)
    placement.element.addEventListener('pointerup', onUp)
    placement.element.addEventListener('pointercancel', onUp)
  }

  continueDrag(event, placement, pageInfo, offsetCssX, offsetCssY) {
    const pageRect = pageInfo.canvas.getBoundingClientRect()
    const dynamicScale = pageRect.width / pageInfo.pdfWidth
    const cssX = event.clientX - pageRect.left - offsetCssX
    const cssY = event.clientY - pageRect.top - offsetCssY
    const ptX = cssX / dynamicScale
    const ptYFromTop = cssY / dynamicScale

    const maxX = pageInfo.pdfWidth - placement.width
    const maxY = pageInfo.pdfHeight - placement.height
    placement.x = clamp(ptX, 0, maxX)
    placement.y = clamp(
      pageInfo.pdfHeight - ptYFromTop - placement.height,
      0,
      maxY
    )
    this.reprojectPlacement(placement)
  }

  beginResize(event, placement, direction) {
    if (event.button !== 0) return
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo) return

    event.preventDefault()
    const handle = event.currentTarget
    const startState = {
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
      cssX: event.clientX,
      cssY: event.clientY,
    }

    const onMove = (moveEvent) => {
      this.continueResize(moveEvent, placement, direction, pageInfo, startState)
    }
    const onUp = (upEvent) => {
      try {
        handle.releasePointerCapture(upEvent.pointerId)
      } catch (_) { /* noop */ }
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onUp)
      this.dragging = false
      this.notifyPlacementsChange()
    }

    this.dragging = true
    handle.setPointerCapture(event.pointerId)
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onUp)
  }

  continueResize(event, placement, direction, pageInfo, startState) {
    const pageRect = pageInfo.canvas.getBoundingClientRect()
    const dynamicScale = pageRect.width / pageInfo.pdfWidth
    const dxPt = (event.clientX - startState.cssX) / dynamicScale
    const dyPt = (event.clientY - startState.cssY) / dynamicScale

    const minW = this.minWidthValue || 80
    const maxW = this.maxWidthValue || 400
    const minH = this.minHeightValue || 30
    const maxH = this.maxHeightValue || 160

    let newW = startState.width
    let newH = startState.height

    if (direction === 'nw' || direction === 'sw') {
      newW = startState.width - dxPt
    } else {
      newW = startState.width + dxPt
    }
    if (direction === 'nw' || direction === 'ne') {
      newH = startState.height - dyPt
    } else {
      newH = startState.height + dyPt
    }

    newW = clamp(newW, minW, Math.min(maxW, pageInfo.pdfWidth))
    newH = clamp(newH, minH, Math.min(maxH, pageInfo.pdfHeight))

    let newX = startState.x
    let newY = startState.y

    if (direction === 'nw' || direction === 'sw') {
      newX = startState.x + (startState.width - newW)
    }
    if (direction === 'se' || direction === 'sw') {
      newY = startState.y - (newH - startState.height)
    }

    placement.x = clamp(newX, 0, pageInfo.pdfWidth - newW)
    placement.y = clamp(newY, 0, pageInfo.pdfHeight - newH)
    placement.width = newW
    placement.height = newH
    this.reprojectPlacement(placement)
  }

  removePlacement(placement) {
    const index = this.placements.indexOf(placement)
    if (index >= 0) this.placements.splice(index, 1)
    if (placement.element) placement.element.remove()
    this.refreshChipBadges()
    this.refreshCounter()
    this.notifyPlacementsChange()
  }

  reprojectPlacement(placement) {
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo || !placement.element) return
    const projection = computeProjection(placement, pageInfo)
    applyProjection(placement.element, projection)

    const fontPx = clamp(projection.cssHeight * 0.22, 10, 18)
    placement.element.style.fontSize = `${fontPx}px`
  }

  refreshChipBadges() {
    if (!this.hasChipsListTarget) return

    const placedRefs = new Set(this.placements.map((p) => p.signerRef))
    this.chipsListTarget
      .querySelectorAll('[data-signer-ref]')
      .forEach((chip) => {
        const ref = chip.dataset.signerRef
        const placed = placedRefs.has(ref)
        chip.dataset.signerPlaced = String(placed)
        chip.classList.toggle('opacity-50', placed)

        const existingBadge = chip.querySelector('[data-placed-badge]')
        if (placed && !existingBadge) {
          chip.appendChild(this.buildPlacedBadge())
        } else if (!placed && existingBadge) {
          existingBadge.remove()
        }
      })
  }

  buildPlacedBadge() {
    const badge = document.createElement('span')
    badge.dataset.placedBadge = ''
    badge.textContent = this.placedLabelValue
    badge.className = [
      'absolute top-1 right-1 inline-flex items-center px-1.5 py-0.5',
      'rounded-full text-[10px] font-medium uppercase tracking-wide',
      'bg-green-50 text-green-700 border border-green-200',
    ].join(' ')
    return badge
  }

  refreshCounter() {
    if (!this.hasCounterTarget) return

    const placedRefs = new Set(this.placements.map((p) => p.signerRef))
    const total = (this.signersValue || []).length
    const placedCount = (this.signersValue || []).reduce(
      (memo, signer) => memo + (placedRefs.has(signer.ref) ? 1 : 0),
      0
    )

    const text = interpolate(
      this.counterTemplateValue,
      { count: placedCount, total: total }
    )
    this.counterTarget.textContent = text

    const allPlaced = total > 0 && placedCount === total
    this.counterTarget.classList.toggle('text-green-700', allPlaced)
    this.counterTarget.classList.toggle('font-medium', allPlaced)
    this.counterTarget.classList.toggle('text-gray-500', !allPlaced)
  }

  labelForRef(ref) {
    const signer = (this.signersValue || []).find((s) => s.ref === ref)
    if (!signer) return ref

    const baseName = signer.name || interpolate(
      this.unnamedLabelValue,
      { index: signer.index + 1 }
    )
    if (this.modeValue === 'sequential') {
      return interpolate(this.sequentialLabelValue, {
        name: baseName,
        index: signer.index + 1,
      })
    }
    return baseName
  }

  colorForRef(ref) {
    const signer = (this.signersValue || []).find((s) => s.ref === ref)
    return signer?.color || '#028090'
  }

  notifyPlacementsChange() {
    const detail = {
      documentId: this.documentIdValue,
      placements: this.placements.map((placement) => ({
        document_id: this.documentIdValue,
        signer_ref: placement.signerRef,
        page_number: placement.page,
        x: Math.round(placement.x),
        y: Math.round(placement.y),
        width: Math.round(placement.width),
        height: Math.round(placement.height),
      })),
    }
    const event = new CustomEvent(
      'document-placer:placements-changed',
      { detail, bubbles: true }
    )
    document.dispatchEvent(event)
  }
}
